import React, { useState, useEffect, useCallback } from 'react';
import { AuditLogService } from '@/services/audit-log.service';
import {
  AuditLog,
  AuditLogAction,
  AuditLogSeverity,
  AuditLogFilter,
  ActionLabels,
  SeverityLabels,
} from '@/models/audit-log.model';


export const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<AuditLogFilter>({});
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AuditLogService.getAuditLogs(filter, 200);
      setLogs(data.logs);
      setFilteredLogs(data.logs);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'Action', 'Severity', 'User', 'Entity', 'Description', 'Success', 'IP Address'].join(','),
      ...filteredLogs.map(log => [
        log.timestamp.toISOString(),
        ActionLabels[log.action],
        SeverityLabels[log.severity],
        log.userName || log.userEmail || 'Unknown',
        log.entityName || log.entityId || 'N/A',
        `"${log.description.replace(/"/g, '""')}"`,
        log.success ? 'Yes' : 'No',
        log.ipAddress || 'N/A',
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
            <p className="mt-1 text-sm text-gray-500">Complete audit trail of all system activities</p>
          </div>
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={filter.searchQuery || ''}
                onChange={(e) => setFilter({ ...filter, searchQuery: e.target.value || undefined })}
                placeholder="Search logs..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <select
                value={filter.action || 'all'}
                onChange={(e) => setFilter({ ...filter, action: e.target.value === 'all' ? undefined : e.target.value as AuditLogAction })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Actions</option>
                {Object.entries(ActionLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
              <select
                value={filter.severity || 'all'}
                onChange={(e) => setFilter({ ...filter, severity: e.target.value === 'all' ? undefined : e.target.value as AuditLogSeverity })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Severities</option>
                {Object.entries(SeverityLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filter.success === undefined ? 'all' : filter.success ? 'success' : 'failed'}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilter({
                    ...filter,
                    success: value === 'all' ? undefined : value === 'success',
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={filter.startDate ? filter.startDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setFilter({ ...filter, startDate: e.target.value ? new Date(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={filter.endDate ? filter.endDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setFilter({ ...filter, endDate: e.target.value ? new Date(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Logs List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Audit Logs ({filteredLogs.length})
            </h3>
          </div>
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredLogs.map((log) => (
                <LogRow
                  key={log.id}
                  log={log}
                  onView={() => setSelectedLog(log)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <LogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
};

interface LogRowProps {
  log: AuditLog;
  onView: () => void;
}

const LogRow: React.FC<LogRowProps> = ({ log, onView }) => {
  const getSeverityColorClass = (severity: AuditLogSeverity) => {
    switch (severity) {
      case AuditLogSeverity.critical:
        return 'bg-red-100 text-red-800';
      case AuditLogSeverity.error:
        return 'bg-orange-100 text-orange-800';
      case AuditLogSeverity.warning:
        return 'bg-yellow-100 text-yellow-800';
      case AuditLogSeverity.info:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer" onClick={onView}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSeverityColorClass(log.severity)}`}>
              {SeverityLabels[log.severity]}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {ActionLabels[log.action]}
            </span>
            {!log.success && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                Failed
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600">{log.description}</p>
          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
            {log.userName && <span>User: {log.userName}</span>}
            {log.entityName && (
              <>
                <span>•</span>
                <span>Entity: {log.entityName}</span>
              </>
            )}
            {log.ipAddress && (
              <>
                <span>•</span>
                <span>IP: {log.ipAddress}</span>
              </>
            )}
            <span>•</span>
            <span>{new Date(log.timestamp).toLocaleString()}</span>
          </div>
        </div>
        <div className="ml-4">
          {log.success ? (
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

interface LogDetailModalProps {
  log: AuditLog;
  onClose: () => void;
}

const LogDetailModal: React.FC<LogDetailModalProps> = ({ log, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Audit Log Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Action</label>
              <p className="mt-1 text-sm text-gray-900">{ActionLabels[log.action]}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Description</label>
              <p className="mt-1 text-sm text-gray-900">{log.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Severity</label>
                <p className="mt-1 text-sm text-gray-900">{SeverityLabels[log.severity]}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1 text-sm text-gray-900">{log.success ? 'Success' : 'Failed'}</p>
              </div>
            </div>
            {log.userName && (
              <div>
                <label className="text-sm font-medium text-gray-500">User</label>
                <p className="mt-1 text-sm text-gray-900">{log.userName} ({log.userEmail})</p>
              </div>
            )}
            {log.entityName && (
              <div>
                <label className="text-sm font-medium text-gray-500">Entity</label>
                <p className="mt-1 text-sm text-gray-900">{log.entityName} ({log.entityType})</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              {log.ipAddress && (
                <div>
                  <label className="text-sm font-medium text-gray-500">IP Address</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{log.ipAddress}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">Timestamp</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(log.timestamp).toLocaleString()}</p>
              </div>
            </div>
            {log.errorMessage && (
              <div>
                <label className="text-sm font-medium text-gray-500">Error Message</label>
                <p className="mt-1 text-sm text-red-600">{log.errorMessage}</p>
              </div>
            )}
            {log.details && Object.keys(log.details).length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500">Details</label>
                <pre className="mt-1 text-xs bg-gray-50 p-3 rounded overflow-auto">
                  {JSON.stringify(log.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

